import {
  createBankAccount,
  updateBankAccount,
  useBankAccount,
  useBankAccountFieldsConfig,
  useBusinessEntity,
} from '@commenda/api';
import { IBankAccountCredentials } from '@commenda/types';
import { Country } from '@prisma/client';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  getDefaultCurrency,
  validateBankAccountFields,
} from '../form-builder/config/country-bank-utils';
import { useNotification } from '../hooks/useNotification';
import { BankAccountFields } from './BankAccountFields';

export interface BankAccountFormNewHandle {
  submit: () => Promise<void>;
  isSubmitting: boolean;
}

export const BankAccountFormNew = forwardRef<
  BankAccountFormNewHandle,
  {
    corporationId: number;
    onClose: () => void;
    onSaveBankAccount?: (bankAccountId?: string) => void;
    addAsRemittanceAccount?: boolean;
    defaultCountry?: string;
    showCountrySelector?: boolean;
    /** When provided, the form is in edit mode and patches the existing account. */
    bankAccountId?: string;
    initialValues?: {
      accountHolderName?: string;
      country?: string;
      bankName?: string;
      currency?: string;
      credentials?: IBankAccountCredentials;
    };
  }
>(
  (
    {
      corporationId,
      onClose,
      onSaveBankAccount,
      addAsRemittanceAccount = false,
      defaultCountry,
      showCountrySelector = true,
      bankAccountId,
      initialValues,
    },
    ref
  ) => {
    const isEditMode = !!bankAccountId;
    const { data: businessEntity } = useBusinessEntity(corporationId);
    const corporationName = businessEntity?.generalCorporationInfo.legalName;
    const incorporationCountry =
      businessEntity?.generalCorporationInfo.incorporationCountry;

    const [base, setBase] = useState({
      accountHolderName: initialValues?.accountHolderName ?? '',
      country: initialValues?.country ?? defaultCountry ?? '',
      bankName: initialValues?.bankName ?? '',
      currency:
        initialValues?.currency ??
        getDefaultCurrency(initialValues?.country ?? defaultCountry ?? ''),
    });
    // Credentials are stored/returned as-is (snake_case keys matching the JSON form config)
    const [credentials, setCredentials] = useState<Record<string, string>>(
      initialValues?.credentials
        ? (initialValues.credentials as unknown as Record<string, string>)
        : {}
    );
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Prefill account holder name from entity (create mode only)
    useEffect(() => {
      if (isEditMode || !corporationName) return;
      setBase((prev) => {
        if (prev.accountHolderName) return prev;
        return { ...prev, accountHolderName: corporationName };
      });
    }, [corporationName, isEditMode]);

    // Backfill country from incorporation country (create mode only)
    useEffect(() => {
      if (isEditMode || !incorporationCountry) return;
      setBase((prev) => {
        if (prev.country) return prev;
        return {
          ...prev,
          country: incorporationCountry,
          currency: getDefaultCurrency(incorporationCountry),
        };
      });
    }, [incorporationCountry, isEditMode]);

    const { mutate: refetchBankAccounts } = useBankAccount({ corporationId });
    const { config } = useBankAccountFieldsConfig();
    const { setNotification } = useNotification();

    // In edit mode, strip credential keys not in the JSON config so legacy fields
    useEffect(() => {
      if (!isEditMode || !config) return;
      const validKeys = new Set(Object.keys(config.field_definitions));
      setCredentials((prev) => {
        const filtered = Object.fromEntries(
          Object.entries(prev).filter(([key]) => validKeys.has(key))
        );
        if (Object.keys(filtered).length === Object.keys(prev).length)
          return prev;
        return filtered;
      });
    }, [config, isEditMode]);

    const handleBaseFieldChange = (
      field: 'accountHolderName' | 'country' | 'bankName' | 'currency',
      value: string
    ) => {
      setBase((prev) => ({ ...prev, [field]: value }));
      if (field === 'country') setCredentials({});
    };

    const handleSubmit = async () => {
      if (!config) return;
      const validationErrors = validateBankAccountFields(
        config,
        base,
        credentials
      );
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        const dtoCredentials: Record<string, string> = {};
        Object.entries(credentials).forEach(([key, rawValue]) => {
          const value = rawValue?.trim();
          if (value) dtoCredentials[key] = value;
        });

        if (isEditMode) {
          const result = await updateBankAccount(bankAccountId, {
            accountHolderName: base.accountHolderName.trim(),
            bankName: base.bankName.trim(),
            country: base.country as Country,
            currency: base.currency,
            credentials:
              Object.keys(dtoCredentials).length > 0
                ? (dtoCredentials as Partial<IBankAccountCredentials>)
                : undefined,
          });
          if (result.success) {
            await refetchBankAccounts();
            onSaveBankAccount?.(bankAccountId);
            onClose();
          } else {
            setNotification({
              title: 'Error',
              message: 'Failed to update bank account. Please try again.',
              show: true,
              valid: false,
            });
          }
        } else {
          const result = await createBankAccount(
            {
              corporationId: +corporationId,
              accountHolderName: base.accountHolderName.trim(),
              bankName: base.bankName.trim(),
              country: base.country as Country,
              currency: base.currency,
              isRemittanceAccount: addAsRemittanceAccount,
              credentials:
                Object.keys(dtoCredentials).length > 0
                  ? dtoCredentials
                  : undefined,
            },
            undefined
          );
          if (result?.success && result?.data?.bankAccount?.id) {
            await refetchBankAccounts();
            onSaveBankAccount?.(result.data.bankAccount.id);
            onClose();
          } else {
            setNotification({
              title: 'Error',
              message: 'Failed to add bank account. Please try again.',
              show: true,
              valid: false,
            });
          }
        }
      } catch (error) {
        console.error('Error saving bank account:', error);
        setNotification({
          title: 'Error',
          message:
            'An unexpected error occurred. Please contact Commenda support.',
          show: true,
          valid: false,
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    useImperativeHandle(ref, () => ({ submit: handleSubmit, isSubmitting }), [
      handleSubmit,
      isSubmitting,
    ]);

    return (
      <BankAccountFields
        accountHolderName={base.accountHolderName}
        country={base.country}
        bankName={base.bankName}
        currency={base.currency}
        credentials={credentials}
        errors={errors}
        onBaseFieldChange={handleBaseFieldChange}
        onCredentialChange={(key, val) =>
          setCredentials((prev) => ({ ...prev, [key]: val }))
        }
        onErrorChange={setErrors}
        bankCountryScope={{ mode: showCountrySelector ? 'any' : 'locked' }}
      />
    );
  }
);

BankAccountFormNew.displayName = 'BankAccountFormNew';
